import { useCallback, useEffect, useRef, useState } from 'react';
import { LocaleProvider, useT, isLocale } from './i18n';
import Layout, { type NavView } from './components/Layout';
import ConsentGate from './components/ConsentGate';
import Home from './pages/Home';
import Login from './pages/Login';
import Account from './pages/Account';
import Upload from './pages/Upload';
import Documents from './pages/Documents';
import Billing from './pages/Billing';
import Subscription from './pages/Subscription';
import Pricing from './pages/Pricing';
import Signup from './pages/Signup';
import Invite from './pages/Invite';
import Quota from './pages/Quota';
import Team from './pages/Team';
import Clientes from './pages/Clientes';
import CatalogoMunicipios from './pages/CatalogoMunicipios';
import SolicitarProvedor from './pages/SolicitarProvedor';
import SefazSearch from './pages/SefazSearch';
import SefazStatus from './pages/SefazStatus';
import SefazDfe from './pages/SefazDfe';
import SefazCerts from './pages/SefazCerts';
import { ClienteAtivoProvider } from './contexts/ClienteAtivoContext';
import AdminPanel from './pages/AdminPanel';
import Audit from './pages/Audit';
import TenantSettings from './pages/TenantSettings';
import Onboarding from './pages/Onboarding';
import Legal from './pages/Legal';
import InstallApp from './pages/InstallApp';
import { InstallFloatButton } from './components/InstallFloatButton';
import {
  ApiError,
  type UserMe,
  clearToken,
  fetchMe,
  getToken,
} from './services/api';

function App() {
  const [view, setView] = useState<NavView>('home');
  const prevViewRef = useRef<NavView>('home');
  const [user, setUser] = useState<UserMe | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [consentOk, setConsentOk] = useState(true);
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  // Issue #558: true quando a verificacao de sessao falhou por erro
  // transitorio (5xx/rede) mesmo apos os retries — token e MANTIDO e a UI
  // oferece "Tentar novamente" em vez de derrubar para login.
  const [sessionCheckFailed, setSessionCheckFailed] = useState(false);
  const mountedRef = useRef(true);

  // Issue #558: verifica a sessao persistida. Antes, QUALQUER erro no
  // fetchMe() (500, timeout, API reiniciando, falha de rede) descartava o
  // token e forcava login — Ctrl+R com API indisponivel matava a sessao.
  // Agora o token so e descartado quando a credencial e rejeitada de fato
  // (401/403 — ApiError carrega o status desde handle()). Erro transitorio
  // dispara ate 2 retries automaticos com backoff (2s, 5s); persistindo a
  // falha, o token e mantido e a tela oferece retry manual.
  const checkSession = useCallback(async () => {
    setSessionCheckFailed(false);
    setAuthReady(false);
    const backoffMs = [2000, 5000];
    for (let attempt = 0; ; attempt++) {
      try {
        const u = await fetchMe();
        if (!mountedRef.current) return;
        setUser(u);
        setConsentOk(false);
        setAuthReady(true);
        return;
      } catch (err) {
        if (!mountedRef.current) return;
        const status = err instanceof ApiError ? err.status : 0;
        if (status === 401 || status === 403) {
          // Credencial rejeitada pela API — sessao encerrada de fato.
          clearToken();
          setAuthReady(true);
          return;
        }
        if (attempt < backoffMs.length) {
          await new Promise((r) => setTimeout(r, backoffMs[attempt]));
          continue;
        }
        // Retries esgotados em erro transitorio: mantem o token — a
        // proxima tentativa (manual ou novo reload) ainda usa a sessao.
        setSessionCheckFailed(true);
        setAuthReady(true);
        return;
      }
    }
  }, []);

  // Restaura sessao a partir de token persistido em localStorage.
  useEffect(() => {
    mountedRef.current = true;
    // Issue #411: detectar URL /invite?token=... na inicializacao (SPA
    // state-based nunca le a URL). Antes de restaurar sessao, extrai o token
    // e mostra a pagina de aceite de convite (sem exigir auth).
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    if (path === '/invite') {
      const token = params.get('token');
      if (token) {
        setInviteToken(token);
        setView('invite');
      } else {
        setView('login');
      }
      setAuthReady(true);
      return; // pula restore de sessao
    }

    const token = getToken();
    if (!token) {
      setAuthReady(true);
      return;
    }
    void checkSession();
    return () => {
      mountedRef.current = false;
    };
  }, [checkSession]);

  const handleLoggedIn = useCallback(() => {
    fetchMe()
      .then((u) => {
        setUser(u);
        setConsentOk(false);
        setView('upload');
      })
      .catch((err) => {
        // Issue #558: so descarta o token recem-obtido se a API rejeitou a
        // credencial (401/403). Erro transitorio mantem a sessao — o
        // usuario ve a tela de login, mas um reload ou novo login
        // recupera a sessao persistida.
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          clearToken();
        }
      });
  }, []);

  const handleLogout = useCallback(() => {
    clearToken();
    setUser(null);
    setConsentOk(true);
    setView('home');
  }, []);

  // Issue #369: sessao expirada (401) — o cliente HTTP (api.ts) limpa o
  // token persistido e dispara o evento global 'auranota:session-expired'.
  // O Layout escuta esse evento para mostrar o banner, MAS o estado `user`
  // (que controla o menu e monta o <Notifications/> com polling 30s) vive
  // aqui no App. Sem este listener, `user` nunca era resetado para null e:
  //   - o menu continuava mostrando estado de logado (notificacoes, email,
  //     links Conta/Sair);
  //   - o <Notifications/> continuava montado e fazendo polling a cada 30s
  //     sem token -> 401 -> clearToken() + novo evento em loop. Esse loop
  //     limpava o token recem-obtido pelo login direto no formulario,
  //     deixando o usuario preso ate clicar "Entrar novamente" (que so
  //     limpava o banner por acaso de timing).
  // Resetamos aqui TODO o estado de auth E navegamos para login, garantindo
  // que o login pelo formulario funcione independentemente do banner.
  useEffect(() => {
    const onSessionExpired = () => {
      // Issue #416: NAO redirecionar para login se estiver na tela de
      // convite (/invite). O usuario pode estar aceitando um convite sem
      // estar logado, e um 401 residual nao deve roubar a tela de convite.
      if (window.location.pathname === '/invite') return;
      setUser(null);
      setConsentOk(true);
      setView('login');
    };
    window.addEventListener('auranota:session-expired', onSessionExpired);
    return () => window.removeEventListener('auranota:session-expired', onSessionExpired);
  }, []);

  const handleConsentResolved = useCallback(() => {
    setConsentOk(true);
  }, []);

  const handleNavigate = useCallback((v: NavView) => {
    // Guard: paginas protegidas exigem login
    // Issue #475: subviews SEFAZ tambem sao protegidas (JWT).
    if ((v === 'upload' || v === 'documents' || v === 'subscription' || v === 'billing' || v === 'quota' || v === 'team' || v === 'admin' || v === 'settings' || v === 'audit' || v === 'clientes' || v === 'catalogo' || v === 'request-provider' || v === 'onboarding' || v === 'sefaz' || v === 'sefaz-status' || v === 'sefaz-dfe' || v === 'sefaz-certs') && !getToken()) {
      setView('login');
      return;
    }
    prevViewRef.current = view;
    setView(v);
  }, [view]);

  if (!authReady) {
    return (
      <LocaleProvider preferredLocale={null}>
        <ClienteAtivoProvider>
          <Layout>
            <LoadingText />
          </Layout>
        </ClienteAtivoProvider>
      </LocaleProvider>
    );
  }

  // Issue #558: verificacao de sessao falhou por erro transitorio mesmo
  // apos os retries automaticos. O token foi mantido — a tela bloqueia o
  // app (evita renderizar estado deslogado com sessao valida) e oferece
  // retry manual, que refaz o fetchMe com o mesmo token.
  if (sessionCheckFailed) {
    return (
      <LocaleProvider preferredLocale={null}>
        <ClienteAtivoProvider>
          <Layout>
            <SessionCheckFailed onRetry={() => void checkSession()} />
          </Layout>
        </ClienteAtivoProvider>
      </LocaleProvider>
    );
  }

  // Issue #504: 'custodia' (Termo de Custodia A1) NAO e documento publico —
  // exige login. Fica fora de isLegalView e tem branch propria com `&& user`.
  const isLegalView = view === 'terms' || view === 'privacy' || view === 'cookies';

  return (
    <LocaleProvider
      preferredLocale={
        user?.preferred_language && isLocale(user.preferred_language)
          ? user.preferred_language
          : null
      }
    >
      <ClienteAtivoProvider>
      <InstallFloatButton onNavigate={handleNavigate} />
      <Layout
        currentView={view}
        onNavigate={handleNavigate}
        user={user}
        onLogout={handleLogout}
      >
        {/* Gate de consentimento LGPD pos-login */}
        {user && !consentOk && (
          <ConsentGate onResolved={handleConsentResolved} />
        )}

        {view === 'invite' && inviteToken ? (
          <Invite token={inviteToken} onGoLogin={() => setView('login')} />
        ) : view === 'pricing' ? (
          <Pricing isLoggedIn={!!user} userRole={user?.role} onNavigate={handleNavigate} />
        ) : view === 'signup' ? (
          <Signup onNavigate={handleNavigate} />
        ) : view === 'login' ? (
          <Login onLoggedIn={handleLoggedIn} onNavigate={handleNavigate} />
        ) : view === 'account' && user ? (
          // Edicao de dados pessoais: onUserUpdated mantem o estado do
          // usuario sincronizado apos PATCH /me (ex.: preferred_language
          // alimenta o LocaleProvider e troca o idioma da UI na hora).
          <Account user={user} onUserUpdated={setUser} />
        ) : view === 'upload' && user ? (
          <Upload userRole={user.role} onNavigate={handleNavigate} />
        ) : view === 'documents' && user ? (
          <Documents userRole={user.role} userId={user.id} />
        ) : view === 'subscription' && user ? (
          <Subscription />
        ) : view === 'billing' && user ? (
          <Billing onNavigate={handleNavigate} />
        ) : view === 'quota' && user ? (
          <Quota />
        ) : view === 'team' && user && (user.role === 'admin' || user.role === 'super_user') ? (
          <Team />
        ) : view === 'clientes' && user && (user.role === 'admin' || user.role === 'super_user' || user.role === 'contador') ? (
          <Clientes />
        ) : view === 'catalogo' && user && user.role === 'super_user' ? (
          // Catalogo completo: apenas super_user (decisao de produto).
          // Org user que navega direto cai no fallback (Home) — mesmo
          // comportamento de acesso negado das demais views com role guard.
          <CatalogoMunicipios />
        ) : view === 'request-provider' && user ? (
          // "Solicitar provedor": pagina dos usuarios de org — provedores
          // suportados + sugestao + minhas solicitacoes (+ fila de
          // moderacao para parceiro/super_user). Qualquer usuario logado
          // pode renderizar; a nav so exibe para roles de org.
          <SolicitarProvedor userRole={user.role} />
        ) : view === 'sefaz-status' && user ? (
          <SefazStatus />
        ) : view === 'sefaz' && user ? (
          <SefazSearch onNavigate={handleNavigate} />
        ) : view === 'sefaz-dfe' && user ? (
          <SefazDfe onNavigate={handleNavigate} />
        ) : view === 'sefaz-certs' && user ? (
          <SefazCerts onNavigate={handleNavigate} />
        ) : view === 'admin' && user && user.role === 'super_user' ? (
          <AdminPanel />
        ) : view === 'settings' && user && ['super_user', 'parceiro', 'admin', 'owner'].includes(user.role) ? (
          // Issue #572: userRole prop alimenta o gate do bloco IMAP na
          // aba E-mail (legacy/suporte — so super_user ve; captura
          // dedicada o substitui para roles de org).
          <TenantSettings userRole={user.role} />
        ) : view === 'audit' && user && ['super_user', 'parceiro', 'admin', 'owner', 'auditor'].includes(user.role) ? (
          // Issue #550: Auditoria LGPD exposta aos tenants — mesma
          // AUDIT_ROLES do backend (app/core/roles.py); o escopo da org
          // ativa/impersonada e resolvido pelo JWT + X-Organization-ID.
          <Audit />
        ) : view === 'onboarding' && user && ['super_user', 'parceiro', 'admin', 'owner'].includes(user.role) ? (
          <Onboarding onNavigate={handleNavigate} />
        ) : view === 'install' ? (
          <InstallApp onNavigate={handleNavigate} />
        ) : view === 'custodia' && user ? (
          /* Issue #504: custodia exige login. Deslogado cai no fallback
             (Home), mesmo comportamento de 'account'/'documents'. */
          <Legal view="custodia" onNavigate={handleNavigate} backView={prevViewRef.current} isLoggedIn={!!user} />
        ) : isLegalView ? (
          <Legal view={view as 'terms' | 'privacy' | 'cookies'} onNavigate={handleNavigate} backView={prevViewRef.current} isLoggedIn={!!user} />
        ) : (
          <Home user={user} onNavigate={handleNavigate} />
        )}
      </Layout>
      </ClienteAtivoProvider>
    </LocaleProvider>
  );
}

function LoadingText() {
  const t = useT();
  // Issue #558: enquanto authReady=false o app so pode estar verificando a
  // sessao persistida — texto especifico em vez do generico "Carregando".
  return <p className="text-brand-600">{t('app.checking_session')}</p>;
}

// Issue #558: falha transitoria persistente na verificacao de sessao.
// O token permanece em localStorage; "Tentar novamente" refaz o fetchMe.
function SessionCheckFailed({ onRetry }: { onRetry: () => void }) {
  const t = useT();
  return (
    <div className="mx-auto max-w-md px-4 py-10 text-center">
      <p className="text-brand-900">{t('app.session_check_failed')}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 rounded-md bg-brand-700 px-4 py-2 font-medium text-white hover:bg-brand-800"
      >
        {t('app.try_again')}
      </button>
    </div>
  );
}

export default App;
